import { useStates } from './states';
import sharedService from '../common/services/sharedService';
import { useLayout } from '../layout/composables/layout';
import taxState from '../common/states/taxState';
import supportCodeState from '../common/states/supportCodeState';
import { applyRedirectionRules } from './redirectionRules';
import { TaxCode } from '../common/constants/enums';
import { useDynamicPermissions } from '../composables/useDynamicPermissions';
import { PermissionFlag, PermissionHelper } from '../types/permissions';
import permissionService from '../common/services/permissionService';

const { setActiveMenuItem } = useLayout();

const { userState, onSetInitialData, onShowConfigCompanyData, dynamicPermissionsState } = useStates();

const PUBLIC_ROUTES = [
  'login',
  'register',
  'forgot-password',
  'recover-password',
  'complete-registration',
  'verify-otp',
  'no-connection',
  'subscription-plans', // Permitir acceso a esta ruta sin validaciones adicionales
];

const isNotPublicRoute = (to) => {
  return PUBLIC_ROUTES.every((route) => !to?.name?.includes(route));
};

const isPublicRoute = (to) => {
  return PUBLIC_ROUTES.some((route) => to?.name?.includes(route));
};

async function initializeTaxData() {
  try {
    if (!taxState.hasTaxesData()) {
      const response = await sharedService.getTaxesByType(TaxCode.IVA);
      taxState.setTaxesData((response as any) || []);
    }
  } catch (error) {
    console.error('[RouteGuard] Error initializing tax data:', error);
  }
}

async function initializeSupportCodeData() {
  try {
    if (!supportCodeState.hasSupportCodesData()) {
      const response = await sharedService.getActiveSupportCodes();
      supportCodeState.setSupportCodesData((response as any) || []);
    }
  } catch (error) {
    console.error('[RouteGuard] Error initializing support code data:', error);
  }
}

async function checkConnection() {
  try {
    await sharedService.initialdata();
    return true;
  } catch {
    return false;
  }
}

export async function validateRoute(to, from) {
  const token = sessionStorage.getItem('authToken');
  const lang = localStorage.getItem('lang');

  if (!lang) localStorage.setItem('lang', 'es');

  // Si estamos en la página de no-conexión, verificar si ya hay conexión
  if (to.name === 'no-connection') {
    const hasConnection = await checkConnection();
    if (hasConnection) {
      if (token) {
        try {
          const userData = await sharedService.initialdata();
          onSetInitialData(userData);
          // Initialize tax data and support codes after successful reconnection (non-blocking)
          initializeTaxData();
          initializeSupportCodeData();
          return { name: '' };
        } catch {
          return { name: 'login' };
        }
      } else {
        return { name: 'login' };
      }
    }
    return true;
  }

  if (!token && isNotPublicRoute(to)) {
    return { name: 'login' };
  }

  if (token) {
    if (!userState.data.value) {
      try {
        const userData = await sharedService.initialdata();
        onSetInitialData(userData);
        // Initialize tax data and support codes after successful login (non-blocking)
        initializeTaxData();
        initializeSupportCodeData();
      } catch (error: any) {
        // Si es error de conexión, redirigir a no-connection
        if (!error.response) {
          return { name: 'no-connection' };
        }
        return { name: 'login' };
      }
    }

    // Validar suscripción después de cargar datos del usuario
    const userData = userState.data.value || (null as any);

    // ===== Rutas siempre permitidas (no requieren suscripción ni configuración) =====
    // - subscription-plans: vista para gestionar/contratar
    // - profile: perfil del usuario, siempre accesible
    // - help / no-connection / access-denied: utilitarias
    // - companysettings: necesario para configurar la empresa ANTES de poder suscribirse
    //   (sin esto se produce un bucle: subscription-plans -> companysettings -> subscription-plans ...)
    const SUBSCRIPTION_EXEMPT_ROUTES = [
      'subscription-plans',
      'profile',
      'help',
      'no-connection',
      'access-denied',
      'companysettings',
    ];
    const isSubscriptionExempt = SUBSCRIPTION_EXEMPT_ROUTES.includes(to.name);
    const isPublic = isPublicRoute(to);

    // Si el usuario NO tiene suscripción activa, redirigir a subscription-plans
    // EXCEPTO si la ruta es exenta o es pública (login/register/forgot-password/etc).
    // No tocamos rutas públicas para no romper flujos de invitación (register-user,
    // complete-registration, verify-otp) que pueden ser visitadas con un token previo.
    const subscriptionActive = userData?.subscriptionActive;
    const hasNoActiveSubscription =
      subscriptionActive === false || subscriptionActive === null || subscriptionActive === undefined;

    if (hasNoActiveSubscription && !isSubscriptionExempt && !isPublic) {
      return { name: 'subscription-plans' };
    }

    if (isPublic) {
      // Solo validar que tenga nombre de empresa (logoUrl es opcional)
      if (!userData?.nameCompany || userData?.nameCompany === '') {
        setActiveMenuItem('companysettings');
        onShowConfigCompanyData();
        return { name: 'companysettings' };
      }

      // Para subscription-plans, permitir acceso directo sin redirigir
      if (to.name === 'subscription-plans') {
        return true;
      }

      // Si el usuario está autenticado pero su empresa no tiene suscripción activa,
      // enviarlo a la vista de planes en lugar del dashboard
      if (hasNoActiveSubscription) {
        return { name: 'subscription-plans' };
      }

      return { name: 'dashboard' };
    }

    // Rutas exentas que no son públicas (profile, companysettings, help, access-denied):
    // permitir acceso sin pasar por applyRedirectionRules para que NO redirijan a
    // productos/companysettings cuando el usuario apenas está configurando.
    if (isSubscriptionExempt) {
      return true;
    }

    // Apply redirection rules and initialize tax data in parallel for better performance
    const [redirectionResult] = await Promise.all([
      applyRedirectionRules(to),
      initializeTaxData(),
      initializeSupportCodeData(),
    ]);

    if (redirectionResult !== true) {
      return redirectionResult;
    }

    // ===== VALIDACIÓN DE PERMISOS DINÁMICOS =====
    // Validar acceso a módulos y permisos basados en rol
    if (to.meta?.module || to.meta?.permission) {
      const { hasModuleAccess, hasPermission } = useDynamicPermissions();

      // Módulos del sistema que siempre están disponibles (no requieren suscripción)
      const systemModules = ['GLOBAL', 'CONFIGURATION', 'USERS'];
      const moduleCode = to.meta.module as string;

      // Validar acceso al módulo (suscripción) - excepto módulos del sistema
      if (moduleCode && !systemModules.includes(moduleCode) && !hasModuleAccess(moduleCode)) {
        console.warn(`[RouteGuard] Módulo ${moduleCode} no disponible en suscripción`);
        return { name: 'access-denied', params: { reason: 'module-not-subscribed' } };
      }

      // Validar permiso específico (rol) - SOLO BASE DE DATOS
      if (to.meta.permission && moduleCode) {
        const permissionFlag = to.meta.permission as PermissionFlag;
        
        
        // Si los permisos dinámicos no están cargados, esperar a que se carguen
        if (!dynamicPermissionsState.isLoaded.value) {
          // Esperar máximo 3 segundos a que se carguen los permisos
          const maxWaitTime = 3000;
          const startTime = Date.now();
          while (!dynamicPermissionsState.isLoaded.value && (Date.now() - startTime) < maxWaitTime) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
        
        
        const hasPermissionResult = hasPermission(moduleCode, permissionFlag);
        
        
        if (!hasPermissionResult) {
          console.error(`[RouteGuard] ❌ ACCESO DENEGADO - Sin permiso ${permissionFlag} en módulo ${moduleCode}`);
          return { name: 'access-denied', params: { reason: 'insufficient-permissions' } };
        }
        
      }
    }
  }

  return true;
}
